import {
  DesiredItemState,
  DesiredDesignState,
  MasterLabelDebugInfo,
  MasterLabelDiffSummary,
  MasterLabelReviewIssueType,
  MasterLabelStateSaveRequest,
  MasterUPCData,
} from '../types';

type CropSection = 'design' | 'pending' | 'no-design' | 'carton' | 'inactive';

interface CropPlacement {
  cropId: string;
  itemNumber: string;
  section: CropSection;
  designId: string | null;
  orientation: string | null;
  reviewIssueType: MasterLabelReviewIssueType | null;
  markedForDeletion: boolean;
}

interface DesignSnapshot {
  id: string;
  persistedDesignId: string | null;
  itemNumber: string;
  representativeCropId: string | null;
  cropIds?: string[];
}

interface ItemDiffAccumulator {
  itemNumber: string;
  cropChangeCount: number;
  representativeChangeCount: number;
  draftDesignCount: number;
  dirtyCropIds: Set<string>;
  dirtyDesignIds: Set<string>;
}

function createAccumulator(itemNumber: string): ItemDiffAccumulator {
  return {
    itemNumber,
    cropChangeCount: 0,
    representativeChangeCount: 0,
    draftDesignCount: 0,
    dirtyCropIds: new Set(),
    dirtyDesignIds: new Set(),
  };
}

function ensureAccumulator(
  accByItem: Map<string, ItemDiffAccumulator>,
  itemNumber: string
): ItemDiffAccumulator {
  let acc = accByItem.get(itemNumber);
  if (!acc) {
    acc = createAccumulator(itemNumber);
    accByItem.set(itemNumber, acc);
  }
  return acc;
}

function sortIds(ids: string[]): string[] {
  return [...ids].sort((a, b) => a.localeCompare(b));
}

function normalizeDesignId(designId?: string | null): string | null {
  return designId ?? null;
}

function normalizeOrientation(orientation?: string): string | null {
  return orientation ?? null;
}

function cropPlacementKey(placement: CropPlacement | undefined): string {
  if (!placement) return '__missing__';
  return [
    placement.itemNumber,
    placement.section,
    placement.designId ?? '',
    placement.orientation ?? '',
    placement.reviewIssueType ?? '',
    placement.markedForDeletion ? 'delete' : '',
  ].join('|');
}

function flattenCropPlacements(upcs: MasterUPCData[]): Map<string, CropPlacement> {
  const placements = new Map<string, CropPlacement>();

  for (const upc of upcs) {
    for (const design of upc.designs) {
      for (const crop of design.crops) {
        placements.set(crop.id, {
          cropId: crop.id,
          itemNumber: upc.itemNumber,
          section: 'design',
          designId: normalizeDesignId(design.id),
          orientation: normalizeOrientation(crop.orientation),
          reviewIssueType: crop.reviewIssueType ?? null,
          markedForDeletion: Boolean(crop.markedForDeletion),
        });
      }
    }
    for (const crop of upc.pendingCrops ?? []) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'pending',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
    for (const crop of upc.unassignedCrops) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'no-design',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
    for (const crop of upc.cartonCrops) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'carton',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
    for (const crop of upc.inactiveCrops ?? []) {
      placements.set(crop.id, {
        cropId: crop.id,
        itemNumber: upc.itemNumber,
        section: 'inactive',
        designId: null,
        orientation: normalizeOrientation(crop.orientation),
        reviewIssueType: crop.reviewIssueType ?? null,
        markedForDeletion: Boolean(crop.markedForDeletion),
      });
    }
  }

  return placements;
}

function getAllUpcCrops(upc: MasterUPCData) {
  return [
    ...upc.designs.flatMap((design) => design.crops),
    ...(upc.pendingCrops ?? []),
    ...upc.unassignedCrops,
    ...upc.cartonCrops,
    ...(upc.inactiveCrops ?? []),
  ];
}

function sortReviewCropIds(cropIds: string[]): string[] {
  return [...cropIds].sort((left, right) => left.localeCompare(right));
}

function buildReviewState(upc: MasterUPCData) {
  const missingDesignCropIds: string[] = [];
  const sopUnclearCropIds: string[] = [];
  const otherCropIds: string[] = [];

  for (const crop of getAllUpcCrops(upc)) {
    if (crop.reviewIssueType === 'missing_design') {
      missingDesignCropIds.push(crop.id);
    } else if (crop.reviewIssueType === 'sop_unclear') {
      sopUnclearCropIds.push(crop.id);
    } else if (crop.reviewIssueType === 'other') {
      otherCropIds.push(crop.id);
    }
  }

  return {
    missingDesignCropIds: sortReviewCropIds(missingDesignCropIds),
    sopUnclearCropIds: sortReviewCropIds(sopUnclearCropIds),
    otherCropIds: sortReviewCropIds(otherCropIds),
  };
}

function flattenPersistedDesigns(upcs: MasterUPCData[]): Map<string, DesignSnapshot> {
  const designs = new Map<string, DesignSnapshot>();
  for (const upc of upcs) {
    for (const design of upc.designs) {
      const persistedDesignId = design.persistedDesignId ?? null;
      if (!persistedDesignId) continue;
      designs.set(persistedDesignId, {
        id: design.id,
        persistedDesignId,
        itemNumber: upc.itemNumber,
        representativeCropId: design.representativeCropId,
      });
    }
  }
  return designs;
}

function flattenDraftDesigns(upcs: MasterUPCData[]): DesignSnapshot[] {
  const drafts: DesignSnapshot[] = [];
  for (const upc of upcs) {
    for (const design of upc.designs) {
      if (design.persistedDesignId) continue;
      drafts.push({
        id: design.id,
        persistedDesignId: null,
        itemNumber: upc.itemNumber,
        representativeCropId: design.representativeCropId,
        cropIds: sortIds(design.crops.map((crop) => crop.id)),
      });
    }
  }
  return drafts;
}

function finalizeSummary(accByItem: Map<string, ItemDiffAccumulator>, totalChanges: number): MasterLabelDiffSummary {
  const itemSummaries = [...accByItem.values()]
    .map((acc) => ({
      itemNumber: acc.itemNumber,
      changeCount:
        acc.cropChangeCount +
        acc.representativeChangeCount +
        acc.draftDesignCount,
      cropChangeCount: acc.cropChangeCount,
      representativeChangeCount: acc.representativeChangeCount,
      draftDesignCount: acc.draftDesignCount,
      dirtyCropIds: sortIds([...acc.dirtyCropIds]),
      dirtyDesignIds: sortIds([...acc.dirtyDesignIds]),
    }))
    .filter((summary) => summary.changeCount > 0)
    .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber));

  return {
    totalChanges,
    dirtyItemNumbers: itemSummaries.map((summary) => summary.itemNumber),
    itemSummaries,
  };
}

export function computeMasterLabelDiffSummary(
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelDiffSummary {
  const accByItem = new Map<string, ItemDiffAccumulator>();
  const basePlacements = flattenCropPlacements(baseUpcs);
  const workingPlacements = flattenCropPlacements(workingUpcs);
  const cropIds = new Set([...basePlacements.keys(), ...workingPlacements.keys()]);

  let cropChangeTotal = 0;
  for (const cropId of cropIds) {
    const basePlacement = basePlacements.get(cropId);
    const workingPlacement = workingPlacements.get(cropId);
    if (cropPlacementKey(basePlacement) === cropPlacementKey(workingPlacement)) continue;
    cropChangeTotal += 1;
    for (const itemNumber of new Set([basePlacement?.itemNumber, workingPlacement?.itemNumber].filter(Boolean) as string[])) {
      const acc = ensureAccumulator(accByItem, itemNumber);
      acc.cropChangeCount += 1;
      acc.dirtyCropIds.add(cropId);
    }
  }

  const basePersistedDesigns = flattenPersistedDesigns(baseUpcs);
  const workingPersistedDesigns = flattenPersistedDesigns(workingUpcs);
  const persistedDesignIds = new Set([...basePersistedDesigns.keys(), ...workingPersistedDesigns.keys()]);

  let representativeChangeTotal = 0;
  for (const designId of persistedDesignIds) {
    const baseDesign = basePersistedDesigns.get(designId);
    const workingDesign = workingPersistedDesigns.get(designId);

    if (!baseDesign || !workingDesign) continue;

    if (baseDesign.representativeCropId !== workingDesign.representativeCropId) {
      representativeChangeTotal += 1;
      for (const itemNumber of new Set([baseDesign.itemNumber, workingDesign.itemNumber])) {
        const acc = ensureAccumulator(accByItem, itemNumber);
        acc.representativeChangeCount += 1;
        acc.dirtyDesignIds.add(designId);
      }
    }
  }

  const draftDesigns = flattenDraftDesigns(workingUpcs);
  let draftDesignTotal = 0;
  for (const draftDesign of draftDesigns) {
    draftDesignTotal += 1;
    const acc = ensureAccumulator(accByItem, draftDesign.itemNumber);
    acc.draftDesignCount += 1;
    acc.dirtyDesignIds.add(draftDesign.id);
  }

  return finalizeSummary(
    accByItem,
    cropChangeTotal + representativeChangeTotal + draftDesignTotal
  );
}

function serializeDesignState(design: MasterUPCData['designs'][number]): DesiredDesignState {
  return {
    clientDesignId: design.id,
    _id: design.persistedDesignId ?? null,
    barcodeStatus: design.barcodeStatus,
    representativeCropId: design.representativeCropId || null,
    normalized_barcode: design.normalizedBarcode || null,
    cropIds: sortIds(design.crops.map((crop) => crop.id)),
  };
}

function shouldSerializeEmptyPersistedDesign(design: MasterUPCData['designs'][number]): boolean {
  if (!design.persistedDesignId) return false;
  return design.barcodeStatus !== 'unconfirmed';
}

function collectNotFrontCropIds(upc: MasterUPCData): string[] {
  return sortIds([
    ...upc.designs.flatMap((design) =>
      design.crops
        .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
        .map((crop) => crop.id)
    ),
    ...(upc.pendingCrops ?? [])
      .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
      .map((crop) => crop.id),
    ...upc.unassignedCrops
      .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
      .map((crop) => crop.id),
    ...(upc.inactiveCrops ?? [])
      .filter((crop) => crop.classificationType !== 'carton' && crop.orientation === 'not-front')
      .map((crop) => crop.id),
  ]);
}

function serializeDesiredItemState(upc: MasterUPCData): DesiredItemState {
  return {
    item_number: upc.itemNumber,
    snapshotVersion: upc.snapshotVersion || '',
    designs: [...upc.designs]
      .filter((design) => (
        design.crops.length > 0
        || shouldSerializeEmptyPersistedDesign(design)
      ))
      .sort((a, b) => a.id.localeCompare(b.id))
      .map(serializeDesignState),
    pendingCropIds: sortIds((upc.pendingCrops ?? []).map((crop) => crop.id)),
    unassignedCropIds: sortIds(upc.unassignedCrops.map((crop) => crop.id)),
    notFrontCropIds: collectNotFrontCropIds(upc),
    cartonCropIds: sortIds(upc.cartonCrops.map((crop) => crop.id)),
    inactiveCropIds: sortIds((upc.inactiveCrops ?? []).map((crop) => crop.id)),
    review: buildReviewState(upc),
  };
}

export function buildMasterLabelStateSaveRequest(
  chainId: number,
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelStateSaveRequest {
  const diffSummary = computeMasterLabelDiffSummary(baseUpcs, workingUpcs);
  const dirtyItemNumbers = new Set(diffSummary.dirtyItemNumbers);
  return {
    chain_id: chainId,
    items: workingUpcs
      .filter((upc) => dirtyItemNumbers.has(upc.itemNumber))
      .sort((a, b) => a.itemNumber.localeCompare(b.itemNumber))
      .map(serializeDesiredItemState),
    confirmedDeleteCropIds: sortIds(
      workingUpcs
        .filter((upc) => dirtyItemNumbers.has(upc.itemNumber))
        .flatMap((upc) =>
          getAllUpcCrops(upc)
            .filter((crop) => crop.markedForDeletion)
            .map((crop) => crop.id)
        )
    ),
  };
}

export function buildMasterLabelDebugInfo(
  chainId: number,
  baseUpcs: MasterUPCData[],
  workingUpcs: MasterUPCData[]
): MasterLabelDebugInfo {
  const summary = computeMasterLabelDiffSummary(baseUpcs, workingUpcs);
  const request = buildMasterLabelStateSaveRequest(chainId, baseUpcs, workingUpcs);
  const basePlacements = flattenCropPlacements(baseUpcs);
  const workingPlacements = flattenCropPlacements(workingUpcs);
  const cropIds = new Set([...basePlacements.keys(), ...workingPlacements.keys()]);
  const cropChanges = [...cropIds]
    .filter((cropId) => cropPlacementKey(basePlacements.get(cropId)) !== cropPlacementKey(workingPlacements.get(cropId)))
    .sort((left, right) => left.localeCompare(right))
    .map((cropId) => {
      const basePlacement = basePlacements.get(cropId);
      const workingPlacement = workingPlacements.get(cropId);
      return {
        cropId,
        baseItemNumber: basePlacement?.itemNumber ?? null,
        baseSection: basePlacement?.section ?? null,
        baseDesignId: basePlacement?.designId ?? null,
        baseOrientation: basePlacement?.orientation ?? null,
        baseReviewIssueType: basePlacement?.reviewIssueType ?? null,
        baseMarkedForDeletion: basePlacement?.markedForDeletion ?? false,
        nextItemNumber: workingPlacement?.itemNumber ?? null,
        nextSection: workingPlacement?.section ?? null,
        nextDesignId: workingPlacement?.designId ?? null,
        nextOrientation: workingPlacement?.orientation ?? null,
        nextReviewIssueType: workingPlacement?.reviewIssueType ?? null,
        nextMarkedForDeletion: workingPlacement?.markedForDeletion ?? false,
      };
    });

  const basePersistedDesigns = flattenPersistedDesigns(baseUpcs);
  const workingPersistedDesigns = flattenPersistedDesigns(workingUpcs);
  const persistedDesignIds = new Set([...basePersistedDesigns.keys(), ...workingPersistedDesigns.keys()]);
  const representativeChanges = [...persistedDesignIds]
    .map((designId) => {
      const baseDesign = basePersistedDesigns.get(designId);
      const workingDesign = workingPersistedDesigns.get(designId);
      if (!baseDesign || !workingDesign) return null;
      if (baseDesign.representativeCropId === workingDesign.representativeCropId) return null;
      return {
        designId,
        baseItemNumber: baseDesign.itemNumber,
        nextItemNumber: workingDesign.itemNumber,
        baseRepresentativeCropId: baseDesign.representativeCropId,
        nextRepresentativeCropId: workingDesign.representativeCropId,
      };
    })
    .filter((change): change is NonNullable<typeof change> => Boolean(change))
    .sort((left, right) => left.designId.localeCompare(right.designId));

  const draftDesigns = flattenDraftDesigns(workingUpcs)
    .map((design) => ({
      designId: design.id,
      itemNumber: design.itemNumber,
      representativeCropId: design.representativeCropId,
      cropIds: design.cropIds ?? [],
    }))
    .sort((left, right) => left.designId.localeCompare(right.designId));

  return {
    summary,
    request,
    cropChanges,
    representativeChanges,
    draftDesigns,
  };
}
